import type { PageSchema } from "../blocks/types";

/**
 * `/pt/organizacao-de-casamentos/` — BTQ-47, Этап 2, волна A.
 *
 * Спецификация: `btq.pt/pt-core/pt-specs-v2/02-organizacao-de-casamentos.md`.
 * Драфт: `btq.pt/pt-core/pt-pages-v2/02-organizacao-de-casamentos.md`.
 *
 * Пара английской `/services/wedding-planning/`, и форма у пары РАЗНАЯ — это и
 * есть смысл блочного слоя. Английская страница — витрина услуг. Португальский
 * запрос «organização de casamentos» информационный: позицию 1 держит «Como
 * organizar um casamento: guia imprescindível passo a passo», витрин в топ-10
 * нет ни одной (замер 22.08, `serp-checks-2026-08.md`). Поэтому здесь пошаговый
 * календарь, внутри которого стоит предложение, а не наоборот.
 */
export const organizacaoDeCasamentos: PageSchema = {
  en: { kind: "component" },
  pt: {
    kind: "blocks",
    meta: {
      title: "Como Organizar um Casamento em Portugal: Guia Passo a Passo | Event Boutique",
      description:
        "Guia completo: calendário de 12 a 18 meses, documentos e custos do registo civil (120 € a 200 €), modelos de serviço de wedding planner e o que perguntar antes de contratar.",
      headQuery: "organização de casamentos",
    },
    blocks: [
      {
        type: "hero",
        eyebrow: "Guia",
        h1: "Como organizar um casamento em Portugal: guia passo a passo",
        image: "services-ready/svc-planning-couple-consultation-samples.webp",
        imageAlt: "Casal a rever amostras e planta de sala numa reunião de planeamento",
      },
      {
        type: "answerFirst",
        paragraphs: [
          "Organizar um casamento em Portugal leva 12 a 18 meses para uma data de época alta e dezoito a vinte e quatro meses em época alta, entre maio e outubro. Pelo caminho ficam cerca de vinte fornecedores, um processo no registo civil que custa 120 € ou 200 €, e um orçamento que se move a cada decisão.",
          "Este guia mostra a ordem em que as decisões se tomam, quanto custa cada passo e o que se parte quando um deles é saltado. No fim há uma tabela com os modelos de serviço e o que cada um custa no mercado, para quem chegar à conclusão de que não quer fazer isto sozinho.",
        ],
      },
      {
        type: "steps",
        heading: "O calendário, passo a passo",
        intro:
          "Este é o ritmo de um casamento de época alta com convidados que vêm de fora. Fora de época há mais folga nos calendários dos espaços, e prazos mais curtos tornam-se possíveis, sobretudo em casamentos mais íntimos.",
        steps: [
          {
            when: "12 a 18 meses antes",
            title: "A data e o espaço",
            body: [
              "Decide-se a data, a região e o enquadramento de orçamento.",
              "Reserva-se o espaço, e é a peça que fecha mais cedo. As quintas com cozinha própria e alojamento servem um casamento por fim de semana e não mais, por isso os sábados de junho a setembro esgotam primeiro.",
            ],
            money:
              "Sinal do espaço, normalmente entre 20% e 30% do aluguer. É também aqui que o orçamento total ganha a sua forma, porque o espaço decide o catering possível, o número de convidados e o plano B para chuva.",
            risk:
              "A data escolhida deixa de estar disponível nos espaços que servem o formato, e a escolha passa a ser feita sobre o que sobrou.",
          },
          {
            when: "9 a 12 meses antes",
            title: "Os fornecedores principais",
            body: [
              "Decidem-se catering, fotografia, música e decoração.",
              "Reservam-se as equipas de fotografia e vídeo, que fecham agenda com cerca de um ano de antecedência.",
            ],
            money:
              "1 000 € a 3 000 € em fotografia e vídeo, 500 € a 1 200 € em animação e DJ, 1 000 € a 2 500 € em decoração e flores.",
            risk:
              "As escolhas ficam limitadas a quem ainda tem a data livre, e a decoração passa a ser desenhada à pressa, que é onde ela fica cara sem ficar melhor.",
          },
          {
            when: "6 meses antes",
            title: "O processo civil e os convidados",
            body: [
              "Decide-se o formato da cerimónia: civil na conservatória, civil no espaço ou cerimónia simbólica.",
              "Abre-se o processo preliminar e trata-se do alojamento dos convidados, da papelaria e do desenho das mesas. Fazem-se as provas de menu.",
            ],
            money: "120 € ou 200 € de registo civil, mais convenção antenupcial se houver.",
            risk:
              "O processo preliminar tem prazos próprios, e um casal estrangeiro pode descobrir tarde de mais que falta o certificado de capacidade matrimonial.",
          },
          {
            when: "2 meses antes",
            title: "A contagem final",
            body: [
              "Fecha-se a contagem de convidados, as plantas de sala e o cronograma detalhado.",
              "Confirma-se cada fornecedor, por escrito, com hora de chegada e porta de acesso.",
            ],
            money:
              "É aqui que o catering fecha o número, e cada dez convidados a mais ou a menos mexem no total mais do que qualquer decisão de decoração.",
            risk: "A montagem do dia acontece sem guião, e a família passa o casamento a atender o telefone.",
          },
          {
            when: "Semana do casamento",
            title: "A montagem",
            body: [
              "Montagem, ensaio da cerimónia, receção dos convidados que chegam de fora e a direção do próprio dia.",
            ],
            money: "Pagamentos finais aos fornecedores, normalmente contra entrega.",
            risk: "Se este passo não tiver dono, alguém da família fica com ele, e essa pessoa não vai ao casamento.",
          },
        ],
      },
      {
        type: "procedural",
        heading: "O passo legal: casar no civil em Portugal",
        intro: "Esta é a parte que não depende de gosto e onde os prazos são fixos.",
        items: [
          "O processo preliminar abre-se em qualquer Conservatória do Registo Civil ou pelo portal Civil Online, e o casamento deve celebrar-se nos seis meses seguintes ao despacho favorável",
          "120 € — processo e registo de casamento, com a celebração na conservatória dentro do horário de funcionamento",
          "200 € — quando a celebração é fora da conservatória, nesta mas fora do horário de funcionamento, ou ao sábado, domingo ou feriado; este valor substitui os 120 €",
          "+100 € — convenção antenupcial em regime típico, +160 € em regime atípico, +30 € pelo registo da convenção celebrada perante entidade diferente da conservatória",
          "Regimes de bens: comunhão de adquiridos (o regime supletivo, aplicado se nada for declarado), comunhão geral e separação de bens",
          "O valor de 200 € aplica-se com o transporte do conservador assegurado pelos noivos ou mediante acordo sobre essas despesas",
          "Há isenção de emolumentos para quem prove insuficiência económica, mediante documento da autoridade administrativa competente apresentado na conservatória",
        ],
        note:
          "Para um casal estrangeiro acrescem passaporte ou título de residência, certidão do registo de nascimento na forma exigida pela lei do país de origem, certificado de capacidade matrimonial emitido há menos de seis meses — salvo se o país de origem fixar outro prazo — e tradução certificada dos documentos que não estejam em português, inglês, francês ou espanhol. Muitos casais tratam do civil num dia útil e fazem depois uma cerimónia simbólica no espaço escolhido: sai mais barato, dá liberdade total de local e de hora, e separa a burocracia do dia da festa.",
      },
      {
        type: "table",
        heading: "Modelos de serviço e ordem de preço",
        columns: ["Modelo", "O que inclui", "Quando faz sentido", "Ordem de preço no mercado"],
        rows: [
          [
            "Coordenação do dia",
            "Revisão de contratos, guião minuto a minuto, gestão dos fornecedores no local",
            "Está tudo reservado pelo casal e falta quem conduza o dia",
            "450 € a 1 150 €",
          ],
          [
            "Planeamento parcial",
            "Entra a meio: fornecedores que faltam, orçamento e cronograma",
            "O espaço já está fechado, o resto não",
            "1 200 € a 2 500 €",
          ],
          [
            "Planeamento integral",
            "Da data ao fim da festa: espaço, todos os fornecedores, orçamento, direção no dia",
            "Um casamento de um dia, com decisões todas por tomar",
            "3 000 € a 6 000 €",
          ],
          [
            "Destination, vários dias",
            "Tudo o acima, mais alojamento, transferes, programa de vários dias e equipa no local desde a véspera",
            "Convidados de vários países, dois a quatro dias de programa",
            "a partir de 5 000 €, até 12 000 € ou mais",
          ],
        ],
        note: "Há também agências que trabalham a 10% a 15% do orçamento total em vez de um valor fixo.",
      },
      {
        type: "questions",
        heading: "O que perguntar a qualquer wedding planner",
        intro: "Duas perguntas que mudam a primeira reunião. Fazem-se a nós e a qualquer outra equipa.",
        items: [
          { q: "Há um orçamento mínimo?", a: "As produções da Event Boutique começam nos 70 000 € e a maioria dos casais investe entre 90 000 € e 250 000 €, valores que cobrem o casamento inteiro e não apenas o nosso serviço. Abaixo desse intervalo dizemo-lo na primeira conversa e sugerimos um formato mais leve." },
          {
            q: "Quando é que não precisam de nós?",
            a: "Se o casamento é numa quinta com cozinha própria, com convidados quase todos do país e num só dia, uma coordenação apenas no dia costuma chegar. Muitos espaços já incluem um coordenador próprio no pacote, e nesse caso contratar uma equipa externa acrescenta pouco. Preferimos dizê-lo na primeira conversa em vez de deixar o casal descobrir isso três reuniões depois.",
          },
        ],
      },
      {
        type: "audience",
        heading: "Para quem é este serviço",
        items: [
          "Doze a quinze casamentos por ano, com cinquenta a duzentos e cinquenta convidados.",
          "Produções a partir de 70 000 €, com a maioria dos casais entre 90 000 € e 250 000 € — valor que cobre o casamento inteiro, do aluguer do espaço à fotografia, e não apenas o nosso serviço.",
          "Convidados de vários países, mais do que um dia de programa, ou um espaço que exige construção, transporte e uma equipa a operar no local desde a manhã. É aí que o nosso trabalho compensa.",
        ],
      },
      {
        type: "faq",
        heading: "Perguntas frequentes",
        items: [
          {
            q: "Quanto custa contratar quem organize o casamento?",
            a: "Depende do modelo. Coordenação apenas do dia: 450 € a 1 150 €. Planeamento parcial: 1 200 € a 2 500 €. Planeamento integral: 3 000 € a 6 000 €. Casamentos destination de vários dias, com equipa no local desde a véspera: a partir de 5 000 € e até 12 000 € ou mais. Algumas agências trabalham a 10% a 15% do orçamento total em vez de um valor fixo.",
          },
          {
            q: "Qual é a diferença entre planeamento e coordenação no dia?",
            a: "O planeamento acompanha as decisões durante um ano: espaço, fornecedores, orçamento e desenho. A coordenação no dia entra quando tudo isso já está fechado pelo casal e trata do que acontece nas vinte e quatro horas críticas, com o guião minuto a minuto e a gestão dos fornecedores no local. Um casal que já reservou tudo raramente precisa de planeamento, mas precisa de alguém que não seja a família a resolver o dia.",
          },
          {
            q: "Quanto tempo demora organizar um casamento?",
            a: "Doze a dezoito meses para um sábado de época alta, entre maio e outubro. Prazos mais curtos são por vezes possíveis, sobretudo em casamentos mais íntimos, mas dependem dos calendários dos espaços. Com menos de seis meses ainda é possível, mas a escolha fica limitada ao que estiver livre, e isso nota-se mais no espaço do que no resto.",
          },
          {
            q: "Quanto custa casar no civil?",
            a: "O processo e o registo de casamento custam 120 €. São 200 € quando a celebração acontece fora da conservatória, nesta mas fora do horário de funcionamento, ou ao sábado, domingo ou feriado — valor que substitui os 120 €, não acresce. A convenção antenupcial acrescenta 100 € em regime típico, 160 € em regime atípico e 30 € pelo registo da convenção celebrada perante entidade diferente da conservatória. O valor de 200 € aplica-se com o transporte do conservador assegurado pelos noivos ou mediante acordo sobre essas despesas.",
          },
          {
            q: "Um casal estrangeiro pode casar em Portugal?",
            a: "Sim, e não é preciso residir no país. São necessários passaporte ou título de residência, certidão do registo de nascimento na forma exigida pela lei do país de origem, certificado de capacidade matrimonial emitido há menos de seis meses — salvo se o país de origem fixar outro prazo de validade — e tradução certificada dos documentos que não estejam em português, inglês, francês ou espanhol. O processo preliminar abre-se em qualquer conservatória, e o casamento deve celebrar-se nos seis meses seguintes ao despacho favorável.",
          },
          {
            q: "Trabalham com fornecedores nossos?",
            a: "Sim. Se já têm fotógrafo, banda ou florista de confiança, entram no plano como qualquer outro fornecedor. O nosso trabalho passa a ser articular os contratos e o cronograma para que ninguém chegue à montagem sem saber a que horas e por que porta. Quando não há preferências, apresentamos opções com o custo e a diferença concreta entre elas.",
          },
          {
            q: "Acompanham casamentos fora da região de Lisboa?",
            a: "Produzimos em Lisboa, Cascais, Sintra, Porto, Vale do Douro, Alentejo, Comporta, Algarve e Madeira.",
          },
          {
            q: "Em que línguas trabalham?",
            a: "Português, inglês e russo. Nos casamentos com convidados de vários países, a informação prática é enviada nas línguas do grupo.",
          },
        ],
      },
      {
        type: "related",
        heading: "A seguir",
        links: [
          { routeKey: "pt-money-casamento", label: "Quanto custa um casamento em Portugal" },
          { routeKey: "venues", label: "Onde escolher o espaço: quintas para casamentos" },
        ],
      },
      {
        type: "cta",
        heading: "Vamos falar sobre o vosso dia",
        body:
          "A primeira conversa serve para perceber o formato e sair de lá com um enquadramento de orçamento realista, antes de qualquer proposta.",
        routeKey: "contact",
        label: "Falar connosco",
      },
    ],
  },
};
